/**
 * Zoom-dependent aggregation of node markers.
 *
 * Kept free of Leaflet imports so it can be tested without a DOM.
 */

export interface ClusterCell<T> {
  key: string;
  lat: number;
  lon: number;
  nodes: T[];
}

export interface ClusterInput {
  nodeNum: number;
  lat: number | null;
  lon: number | null;
}

/** Above this zoom every node gets its own marker. */
export const CLUSTER_MAX_ZOOM = 9;

/**
 * Buckets nodes into a grid so low zooms stay responsive. A full clustering
 * algorithm is overkill here: the map only ever shows counts, so a grid whose
 * cell size tracks the zoom level is enough.
 */
export function clusterNodes<T extends ClusterInput>(nodes: T[], zoom: number): ClusterCell<T>[] {
  if (zoom >= CLUSTER_MAX_ZOOM) {
    return nodes
      .filter((n) => n.lat !== null && n.lon !== null)
      .map((n) => ({
        key: `n${n.nodeNum}`,
        lat: n.lat!,
        lon: n.lon!,
        nodes: [n],
      }));
  }

  // Roughly 60 screen pixels per cell at the given zoom. The exponent matters:
  // a cell is 256 / 2^exponent pixels wide, and a grid finer than the marker
  // radius makes neighbouring bubbles pile up into one unreadable blob.
  const cellDeg = 360 / 2 ** (zoom + 2);
  const cells = new Map<string, ClusterCell<T>>();

  for (const n of nodes) {
    if (n.lat === null || n.lon === null) continue;
    const gy = Math.floor(n.lat / cellDeg);
    const gx = Math.floor(n.lon / cellDeg);
    const key = `${gy}:${gx}`;
    let cell = cells.get(key);
    if (!cell) {
      cell = { key, lat: 0, lon: 0, nodes: [] };
      cells.set(key, cell);
    }
    cell.lat += n.lat;
    cell.lon += n.lon;
    cell.nodes.push(n);
  }

  for (const cell of cells.values()) {
    cell.lat /= cell.nodes.length;
    cell.lon /= cell.nodes.length;
  }

  return [...cells.values()];
}

/** Marker radius grows a little as the user zooms in. */
export function markerRadius(role: number, zoom: number): number {
  const base = role === 5 ? 7 : role === 4 || role === 2 ? 6 : 5;
  return zoom >= 12 ? base + 1 : Math.max(4, base - 1);
}

/**
 * Cluster bubbles scale with the node count but stay clickable-sized.
 *
 * The ceiling matches the 64 px clustering grid: bigger bubbles would reach
 * into the neighbouring cell and overlap again.
 */
export function clusterRadius(count: number): number {
  return Math.min(26, 9 + Math.log2(Math.max(1, count)) * 2.6);
}

/**
 * Screen radius in pixels of a circle that is `radiusM` metres wide.
 *
 * Web Mercator stretches away from the equator, so a circle in metres covers
 * more pixels further north and the conversion is only exact at latitude 0. That
 * is fine: it answers "is this bubble big enough to hold a number", not "where
 * exactly does the circle end".
 */
export function radiusMToPx(radiusM: number, lat: number, zoom: number): number {
  const metersPerDegreeLon = 111_320 * Math.cos((lat * Math.PI) / 180);
  return (radiusM / metersPerDegreeLon) * ((256 * 2 ** zoom) / 360);
}